import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Reuses component instances for routes marked with `data: { reuse: true }`:
 * navigating away and back re-attaches the cached component instead of
 * recreating it, so no refetch (and no flicker) happens on re-entry.
 * Handles are keyed by tenant, so switching tenant always recreates the
 * component and reloads its data.
 */
export class TenantRouteReuseStrategy implements RouteReuseStrategy {
  private readonly cache = new Map<string, DetachedRouteHandle>();

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.isReusable(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {
    const key = this.cacheKey(route);
    if (key !== null) {
      this.cache.set(key, handle);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.cacheKey(route);
    return key !== null && this.cache.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.cacheKey(route);
    return key === null ? null : (this.cache.get(key) ?? null);
  }

  shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    if (
      future.params['tenantId'] !== undefined &&
      future.params['tenantId'] !== curr.params['tenantId']
    ) {
      return false;
    }
    return future.routeConfig === curr.routeConfig;
  }

  private isReusable(route: ActivatedRouteSnapshot): boolean {
    return route.routeConfig?.data?.['reuse'] === true;
  }

  private cacheKey(route: ActivatedRouteSnapshot): string | null {
    if (!this.isReusable(route) || !route.routeConfig) {
      return null;
    }
    const tenantId = this.findTenantId(route);
    return `${tenantId}|${route.routeConfig.path ?? ''}`;
  }

  private findTenantId(route: ActivatedRouteSnapshot): string {
    let node: ActivatedRouteSnapshot | null = route;
    while (node) {
      const tenantId = node.params['tenantId'];
      if (tenantId !== undefined) return tenantId;
      node = node.parent;
    }
    return '';
  }
}
